import type { ReactNode } from 'react'
import { Box } from '@mui/material'
import { FormattedMessage } from 'react-intl'

import { tokens, shortScreen } from '@configuration/theme'
import ParetosMarkIcon from '@icons/ParetosMark.icon'
import WordmarkIcon from '@icons/Wordmark.icon'

import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher.component'

interface Props {
  /** Top-of-card back affordance (a <BackButton/>), rendered above the heading. */
  back?: ReactNode
  /** Optional heading rendered as the form-card's h1. */
  heading?: ReactNode
  /** Optional sub-text under the heading. */
  subheading?: ReactNode
  /** Root test id — pages pass their own name (e.g. 'login-page'). */
  dataTestId?: string
  showLanguageSwitcher?: boolean
  children: ReactNode
}

const BRAND_FG = tokens.white

// Brand mark + wordmark render at the same size on both panes so the left
// topnav and the right brand pane logos line up.
const MARK_HEIGHT = '22'
const WORDMARK_HEIGHT = '16'

// Split auth layout from the design's Login Page: a white form pane on the
// left (topnav + vertically-centred form card) and the paretos gradient brand
// pane on the right (hidden below 900px). Shared by login, recovery,
// verification, and the error/not-found screens.
const SplitAuthLayout = ({
  back,
  heading,
  subheading,
  dataTestId = 'split-auth-layout',
  showLanguageSwitcher = true,
  children,
}: Props) => (
  <Box
    data-testid={dataTestId}
    sx={{
      minHeight: '100vh',
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      background: tokens.white,
    }}
  >
    {/* ── Form pane ───────────────────────────────────────────────── */}
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        background: tokens.white,
        minHeight: 0,
      }}
    >
      <Box
        component='header'
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // Logo top/sides match the brand pane's 40px padding so left and
          // right logos align on the same baseline.
          padding: { xs: '24px 20px 0', md: '40px 40px 0' },
          flex: 'none',
          [shortScreen.compact]: { padding: '24px 40px 0' },
          [shortScreen.tight]: { padding: '16px 24px 0' },
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: '10px', color: tokens.businessBlack }}
        >
          <ParetosMarkIcon height={MARK_HEIGHT} />
          <WordmarkIcon height={WORDMARK_HEIGHT} />
        </Box>
        {showLanguageSwitcher && <LanguageSwitcher />}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          padding: { xs: '20px', md: '20px 40px 40px' },
          minHeight: 0,
          [shortScreen.compact]: { padding: '16px 40px 24px' },
          [shortScreen.tight]: { padding: '12px 24px 16px' },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 500,
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '28px',
            [shortScreen.compact]: { gap: '20px' },
            [shortScreen.tight]: { gap: '16px' },
          }}
        >
          {back}
          {heading && (
            <Box>
              <Box
                component='h1'
                sx={{
                  fontSize: 32,
                  lineHeight: 1,
                  letterSpacing: '-0.64px',
                  fontWeight: 500,
                  m: '0 0 8px',
                  [shortScreen.compact]: { fontSize: 28 },
                  [shortScreen.tight]: { fontSize: 24 },
                }}
              >
                {heading}
              </Box>
              {subheading && (
                <Box sx={{ color: tokens.businessBlack60, fontSize: 14, lineHeight: 1.4 }}>
                  {subheading}
                </Box>
              )}
            </Box>
          )}
          {children}
        </Box>
      </Box>

      <Box
        component='footer'
        sx={{
          flex: 'none',
          padding: { xs: '16px 20px', md: '45px 40px' },
          fontSize: 12,
          lineHeight: '16px',
          color: tokens.businessBlack60,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          [shortScreen.compact]: { padding: '20px 40px' },
          [shortScreen.tight]: { padding: '12px 24px' },
        }}
      >
        <Box>
          <FormattedMessage
            id='common.shell.footer.copyright'
            values={{ year: new Date().getFullYear() }}
          />
        </Box>
        <Box
          sx={{
            '& a': { color: tokens.businessBlack60, '&:hover': { color: tokens.versatileViolet } },
          }}
        >
          <FormattedMessage
            id='common.shell.footer.privacyAndImprint'
            values={{
              privacy: (
                <a href='https://paretos.com/privacy-policy' target='_blank' rel='noreferrer'>
                  <FormattedMessage id='common.shell.footer.privacy' />
                </a>
              ),
              imprint: (
                <a href='https://paretos.com/imprint' target='_blank' rel='noreferrer'>
                  <FormattedMessage id='common.shell.footer.imprint' />
                </a>
              ),
            }}
          />
        </Box>
      </Box>
    </Box>

    {/* ── Brand pane (hidden < 900px) ─────────────────────────────── */}
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        position: 'relative',
        overflow: 'hidden',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '40px',
        color: BRAND_FG,
        background: tokens.paretosGradient,
        [shortScreen.compact]: { padding: '32px' },
        [shortScreen.tight]: { padding: '24px' },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -120,
          bottom: -120,
          width: 720,
          height: 720,
          opacity: 0.18,
          color: BRAND_FG,
          pointerEvents: 'none',
        }}
      >
        <ParetosMarkIcon width='100%' height='100%' />
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          visibility: 'hidden',
        }}
      >
        <ParetosMarkIcon height={MARK_HEIGHT} />
        <WordmarkIcon height={WORDMARK_HEIGHT} />
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 500,
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          [shortScreen.tight]: { gap: '16px' },
        }}
      >
        <Box
          sx={{
            fontSize: 12,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            color: tokens.white60,
            visibility: 'hidden',
          }}
        >
          <FormattedMessage id='login.brandPane.eyebrow' />
        </Box>
        <Box
          component='h2'
          sx={{
            fontSize: 56,
            lineHeight: 1.02,
            letterSpacing: '-1.12px',
            fontWeight: 500,
            m: 0,
            [shortScreen.compact]: { fontSize: 44 },
            [shortScreen.tight]: { fontSize: 36 },
          }}
        >
          <FormattedMessage
            id='login.brandPane.headline'
            values={{
              alt: (chunks: ReactNode) => (
                <Box component='span' sx={{ fontWeight: 400, opacity: 0.85 }}>
                  {chunks}
                </Box>
              ),
              br: () => <br />,
            }}
          />
        </Box>
        <Box
          sx={{
            fontSize: 16,
            lineHeight: 1.4,
            color: tokens.white60,
            maxWidth: 380,
            visibility: 'hidden',
          }}
        >
          <FormattedMessage id='login.brandPane.description' />
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          borderTop: `1px solid ${tokens.white20}`,
          paddingTop: '24px',
          visibility: 'hidden',
        }}
      >
        {(['stat1', 'stat2', 'stat3'] as const).map((stat) => (
          <Box key={stat} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box
              sx={{
                fontFamily: tokens.fontMono,
                fontVariantNumeric: 'tabular-nums',
                fontSize: 24,
                lineHeight: 1,
                fontWeight: 600,
                letterSpacing: '-0.5px',
              }}
            >
              <FormattedMessage id={`login.brandPane.${stat}.value`} />
            </Box>
            <Box sx={{ fontSize: 12, color: tokens.white60, letterSpacing: '0.3px' }}>
              <FormattedMessage id={`login.brandPane.${stat}.label`} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
)

export default SplitAuthLayout
